import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DEF_PG_SIZE_FILTER } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { PaginationContext } from '../../contexts/table/PaginationContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { Filter } from '../../models/Filter';
import { Lookup } from '../../models/Lookup';
import { showError } from '../../services/utilities/ServiceError';
import { appendFilter } from '../../utilities/filters';
import { buildSearchTerm } from '../../utilities/SearchDescriptors';
import Chip from '../chip/chip';
import OutsideClick from '../outside-click/outside-click';
import PaginatedSearchableList from '../selection-field/paginated-searchable-list';
import Tooltip from '../Tooltip/Tooltip';
import closeIcon from './../../images/blue_close.svg';

export interface PaginatedFilterChipProps {
    accessor: string;
    filters: Filter[];
    lookupList: Lookup[];
    handleItemClick: any;
    removeFilterWithKey: any;
    toggleFilterModal: (trigger: string) => void;
    disableFilterToggling: boolean
}

export const pageSize = DEF_PG_SIZE_FILTER
export const initialPagination = {
    totalCount: pageSize,
    maxPageSize: pageSize,
    totalPages: 1,
    pageNumber: 1
};

export const PaginatedFilterChip: FC<PaginatedFilterChipProps> = ({ accessor, filters, lookupList, handleItemClick, removeFilterWithKey, toggleFilterModal, disableFilterToggling = false }) => {
    const [filterPagination, setFilterPagination] = useState(initialPagination);
    const [isOpen, setIsOpen] = useState(false);
    const [listItems, setListItems] = useState(new Array(0));
    const [selectedItems, setSelectedItems] = useState(new Array(0));
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const isInitialMount = useRef(true);
    const filter = useMemo(() => filters.find((filter) => filter.key === accessor), [filters, accessor]);
    const lookup = useMemo(() => lookupList.find(item => accessor.startsWith(item.name)), [lookupList, accessor]);
    const { translate } = useLocale();

    const getData = useCallback(async () => {
        if (!lookup || isLoading) {
            return;
        }
        setIsLoading(true);
        try {             
            const newSearchTerm = lookup.name && searchTerm ? buildSearchTerm(lookup.searchAccessor, searchTerm) : undefined;
            let filtersToApply: Filter[] = [];

            if (lookup.filterDependentKey) {
                const dependentFilter = filters.find(f => f.key === lookup.filterDependentKey);
                const selectedValues = dependentFilter?.selected.map(item => item.value).join(",");
                
                if (selectedValues) {
                    filtersToApply = appendFilter(selectedValues, lookup.filterDependentKey, lookup.filterDependentKey, []);
                }
            }

            const { rows, pagination } = lookup.dataMethod
                ? await lookup.dataMethod({
                    filters: filtersToApply,
                    goToPage: filterPagination.pageNumber,
                    pageSize: filterPagination.maxPageSize,
                    searchTerm: newSearchTerm
                })
                : {
                    rows: lookup.values,
                    pagination: {
                        ...filterPagination,
                        totalCount: lookup.values?.length ?? 0,
                        totalPages: Math.ceil((lookup.values?.length ?? 1) / pageSize)
                    }
                };

            setListItems(rows);
            setFilterPagination(pagination);
            setSelectedItems(mapOptionsToSelectedIndexes(filter ?? {}, rows));
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setIsLoading(false);
        }
    }, [lookup, searchTerm, filterPagination.pageNumber, filterPagination.maxPageSize, filter, toast]);

    useEffect(() => {
        if (!lookup || isLoading) {
            return;
        }
        // Only call getData if certain conditions are met
        if (isInitialMount.current || (searchTerm !== undefined && searchTerm.length >= 0) || filterPagination.pageNumber !== 1) {
            getData();
            isInitialMount.current = false;  // Set isInitialMount to false after the first render
        }
    }, [getData, lookup, searchTerm, filterPagination.pageNumber]);

    useEffect(() => {        
        if (isOpen && listItems.length > 0) {
            const filter = filters.find((filter) => filter.key === accessor);
            if (filter) {
                const newSelectedItems = mapOptionsToSelectedIndexes(filter, listItems);
                setSelectedItems(newSelectedItems ?? []);
            }
        }
    }, [isOpen, filter, accessor, listItems]);

    const mapOptionsToSelectedIndexes = (filter, options) => {
        return options.map((option) => filter.selected?.some((item) => item.value === option.value) ? option.value : -1).filter((index) => index >= 0);
    };

    const handleSearch = (searchTerm) => {        
        setSearchTerm(searchTerm);
        setFilterPagination(prev => ({ ...prev, pageNumber: 1 }));
    };

    const toggleOpen = () => {
        // If this filter depends on another, refetch the data to always be up-to-date with latest
        if (lookup && lookup.filterDependentKey) {
            getData();
        }

        setIsOpen(!isOpen);
    };

    const updatePageNumber = (pageNumber: number) => {
        setFilterPagination({ ...filterPagination, pageNumber: pageNumber });
    }

    const isSingleSelect = (): boolean => {
        return lookupList.find(item => accessor.startsWith(item.name))?.isSingleSelect ?? false;
    }

    const isRequired = (): boolean => {
        return filter && filter.selected && filter.selected.length > 0 ? filter.selected[0].value === "require_filter" : false;
    }

    return <Chip className={`flex flex-row items-center py-x text-portal-blue-primary ml-2 filter-chip mb-2 relative  ${isRequired() ? `border-red-700 border-2` : ''}`}>
        <span className="font-bold px-2" style={{ textOverflow: "ellipsis" }}>{filter?.label}:</span>

        {accessor === "dateOnly" ?
            <button type="button" data-testid="button-PaginatedFilterChip-1" className={`flex flex-row items-center pr-2 ${disableFilterToggling ? "cursor-not-allowed" : ""}`} onMouseDown={() => toggleFilterModal(accessor)}>
                {
                    filter &&
                        filter.selected &&
                        filter.selected.length > 0 ?
                        filter.selected.length > 1 ?
                            translate.byKeyFormatted(TranslationKeys.selectedWithCount, [filter.selected.length]) : filter.selected[0].label.replaceAll("-", "/").replaceAll(";", " - ") : ''
                }
            </button>
            : accessor === "time" ?
                <button type="button" data-testid="button-PaginatedFilterChip-1" className={`flex flex-row items-center pr-2`} onMouseDown={() => toggleFilterModal(accessor)}>
                    {filter && filter.selected && filter.selected.length > 0 ? filter.selected[0].label : ''}
                </button>
                : <>

                    {filter && filter.selected && filter.selected.length > 0 ? (
                        filter.selected.length > 1 ? (
                            <Tooltip show text={translate.byKeyFormatted(TranslationKeys.selectedWithCount, [filter.selected.length])} direction="bottom">
                                <span>{translate.byKeyFormatted(TranslationKeys.selectedWithCount, [filter.selected.length])}</span>
                            </Tooltip>
                        ) : (
                            <Tooltip show text={filter.selected[0].label} direction="bottom">
                                <p
                                    style={{ textOverflow: 'ellipsis', fontSize: '11px', textAlign: 'end' }}
                                    className={`overflow-hidden whitespace-nowrap relative filter-chip-text ${isRequired() ? 'text-red-700' : ''}`}>
                                    {filter.selected[0].label}
                                </p>
                            </Tooltip>
                        )
                    ) : (
                        ''
                    )}

                    <>
                        <button type="button" data-testid="button-PaginatedFilterChip-2" className="flex flex-row items-center px-1" onMouseDown={toggleOpen}>
                            <FontAwesomeIcon className="pl-1 text-portal-blue-primary" size='lg' icon={faChevronDown} />
                        </button>
                        <PaginationContext.Provider value={{ isInUse: true, pagination: filterPagination, updatePageNumber, updatePageSize: () => { } }}>
                            <OutsideClick onClick={() => { if (isOpen) { setIsOpen(!isOpen); } }} >
                                <div className={`w-max left-0 absolute bg-white text-black shadow-md -mr-3 rounded-md px-2 py-2 z-50 ${isOpen ? '' : 'hidden'}`} style={{ top: '2rem' }}>
                                    <PaginatedSearchableList
                                        items={listItems}
                                        onSelect={(_, item) => { handleItemClick(item, accessor, listItems) }}
                                        isStriped={true}
                                        isMultiSelect={isSingleSelect() ? false : true}
                                        selectedItems={selectedItems}
                                        onSearch={handleSearch}
                                        searchable={lookupList.find(item => accessor.startsWith(item.name))?.searchAccessor ? true : false}>
                                    </PaginatedSearchableList>
                                </div>
                            </OutsideClick>
                        </PaginationContext.Provider>
                    </>
                </>
        }
        {
            filter?.isStatic ? null :
                <button className="mx-1 mr-2 cursor-pointer w-5" type='button' onClick={() => {
                    removeFilterWithKey(accessor);
                }}>
                    <img src={closeIcon} alt="" />
                </button>
        }
    </Chip>
};
